import { Button } from '../../components/ui/button';
import { Link } from 'react-router-dom';
import { Cpu, Shield, Target, Clock } from 'lucide-react';
import { motion } from 'framer-motion';

export default function DiagnosticsCoding() {
	return (
		<div>
			<motion.section 
				initial={{ opacity: 0, y: 30 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.8 }}
				className="bg-gradient-to-b from-white to-babyblue/20 border-b gradient-hero"
			>
				<div className="container py-12 md:py-16">
					<motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="text-3xl md:text-4xl font-extrabold text-gradient">Diagnostics & Coding</motion.h1>
					<motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="mt-3 max-w-3xl text-gray-600">Real-time identification of recalibration requirements maintaining highest safety standards. Our AI-driven system interfaces directly with your vehicle's onboard diagnostics for precise coding and calibration.</motion.p>
					<motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="mt-6 flex gap-3">
						<a href="https://getstarted.ubiquityglass.com" target="_blank" rel="noreferrer"><Button variant="gradient">Get Started</Button></a>
						<Link to="/support"><Button variant="outline">Contact Us</Button></Link>
					</motion.div>
				</div>
			</motion.section>

			<motion.section initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.8 }} className="container py-12 md:py-16">
				<div className="grid md:grid-cols-2 gap-12 items-center">
					<div>
						<h2 className="text-2xl md:text-3xl font-bold mb-4">Advanced Vehicle Diagnostics</h2>
						<p className="text-gray-600 mb-6">Our AI-driven system interfaces directly with your vehicle's onboard diagnostics, identifying specific requirements for recalibrations and coding in real time. This ensures that all components—particularly ADAS (Advanced Driver Assistance Systems)—are recalibrated accurately, maintaining the highest safety standards and preserving vehicle functionality.</p>
						<p className="text-gray-600 mb-8">By automating diagnostics and seamlessly integrating with manufacturer specifications, we eliminate the guesswork and delays often associated with traditional calibration methods. Our precise, rapid approach minimizes downtime, reduces costs, and guarantees that your vehicle is road-ready with optimal performance.</p>
						<div className="flex gap-3">
							<a href="https://realm.ubiquityauto.com/order/2dcf3188-0a07-4444-b5f8-065a5dc238bb?access_token=_ha6LYxaO-9HjbYZVCY7P21vuKFFRaQ5" target="_blank" rel="noreferrer"><Button variant="gradient">Learn More</Button></a>
						</div>
					</div>
					<motion.div className="flex justify-center" initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6, delay: 0.2 }}>
						<div className="w-full max-w-md bg-gradient-to-br from-babyblue/20 to-royalpurple/20 rounded-2xl p-8 text-center">
							<Cpu className="w-16 h-16 mx-auto mb-4 text-primary" />
							<h3 className="text-xl font-semibold mb-2">Real-Time Diagnostics</h3>
							<p className="text-gray-600">Direct interface with vehicle onboard systems</p>
						</div>
					</motion.div>
				</div>
			</motion.section>

			<motion.section initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.8 }} className="bg-gray-50 py-12 md:py-16">
				<div className="container">
					<h3 className="text-2xl md:text-3xl font-bold text-center mb-12">Why Choose Our Diagnostics & Coding?</h3>
					<div className="grid md:grid-cols-3 gap-8">
						<div className="text-center">
							<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
								<Target className="w-8 h-8 text-primary" />
							</div>
							<h4 className="text-lg font-semibold mb-3">Real-Time Analysis</h4>
							<p className="text-gray-600">Instant identification of recalibration requirements</p>
						</div>
						<div className="text-center">
							<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
								<Shield className="w-8 h-8 text-primary" />
							</div>
							<h4 className="text-lg font-semibold mb-3">Safety Standards</h4>
							<p className="text-gray-600">Maintains highest safety standards and vehicle functionality</p>
						</div>
						<div className="text-center">
							<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
								<Clock className="w-8 h-8 text-primary" />
							</div>
							<h4 className="text-lg font-semibold mb-3">Optimal Performance</h4>
							<p className="text-gray-600">Guarantees road-ready vehicle with optimal performance</p>
						</div>
					</div>
				</div>
			</motion.section>

			<motion.section initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.8 }} className="animated-gradient particle-container subtle-glow">
				<div className="container py-12 md:py-16 text-white text-center">
					<h3 className="text-2xl font-bold mb-4">Ready for diagnostics & coding?</h3>
					<p className="mb-6 max-w-2xl mx-auto">Ensure your vehicle's systems are properly calibrated with our AI-driven diagnostics and coding services.</p>
					<div className="flex gap-3 justify-center">
						<a href="https://realm.ubiquityauto.com/order/2dcf3188-0a07-4444-b5f8-065a5dc238bb?access_token=_ha6LYxaO-9HjbYZVCY7P21vuKFFRaQ5" target="_blank" rel="noreferrer"><Button className="bg-white text-secondary hover:opacity-90">Start Service</Button></a>
						<Link to="/support"><Button variant="outline" className="border-white text-white hover:bg-white/10 bg-transparent">Contact Sales</Button></Link>
					</div>
				</div>
			</motion.section>
		</div>
	);
}
